import React, { useState } from "react";
import { toast } from "react-toastify";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDndMonitor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';
import { animated, useTransition } from "@react-spring/web";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";

import { AutoGrowInput, Input } from "src/components/input";
import DeleteCustomField from "../deleteCustomField";
import SwitchButton from "src/components/switchButton";
import Actions from "src/components/actionbar";
import Toast from "src/components/toast";

import { guid } from "src/utils/util";

import { useCustomFieldsQuery, useDeleteCustomField } from "src/query/settings/customFields";

import { CustomField, CustomFieldOption } from "src/types/settings/customFields";

import _, { size } from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";

interface DetailViewProps {
  selectedCustomField: CustomField;
  onSelectCustomField: (item: CustomField) => void;
  clearSelectedItem: () => void;
  renderEmptyMessages?: () => React.ReactNode;
  onDelete?: (id: string) => void;
  canDelete: boolean;
}

interface LiveReorderMonitorProps {
  options: CustomFieldOption[];
  setOptions: (opts: CustomFieldOption[]) => void;
  setActiveId: (id: string | null) => void;
}

const DetailView: React.FC<DetailViewProps> = ({ selectedCustomField , onSelectCustomField , clearSelectedItem, renderEmptyMessages, canDelete}) => {

  const transitions = useTransition(selectedCustomField, {
  keys: selectedCustomField?.id || "new",
  from: { opacity: 0, marginTop: 60 },
  enter: { opacity: 1, marginTop: 0 },   
  config: { duration: 400 },
  });

  const [showDeleteSlider, setShowDeleteSlider] = useState(false);

  const deleteCustomFieldMutation = useDeleteCustomField();

  const { refetch: refetchCustomFields } = useCustomFieldsQuery();

  // Handle changes to the selected item
  const handleChange = (key: keyof CustomField, value: any) => {
    onSelectCustomField({
      ...selectedCustomField,
      [key]: value,
    });
  };


  const handleDeleteClick = () => {
    setShowDeleteSlider(true);
  };

  const disabledCustomField = !!selectedCustomField.integrationType

  const handleDeleteSuccess = () => {
    toast(
      <Toast.Sucess
        message={`Custom field "${selectedCustomField.name}" deleted successfully`}
      />,
      { autoClose: 3000 }
    );
    setShowDeleteSlider(false);
    clearSelectedItem();
    refetchCustomFields();
  };

  // Handle confirming deletion of the custom field
  const handleConfirmDelete = () => {
    deleteCustomFieldMutation.mutate(selectedCustomField.id, {
      onSuccess: handleDeleteSuccess,
      onError: () => {
        toast(
          <Toast.Failure message="Failed to delete custom field" />,
          { autoClose: 3000 }
        );
      },
    });
  };

  return (
    <div className={styles.container}>
      {!selectedCustomField && renderEmptyMessages?.()}
      {transitions((style, selectedCustomField) =>
        selectedCustomField && (
          <animated.div style={style} className={styles.detailContent}>
            <div className={styles.header}>
              <div className={styles.inputFields}>
                <Input
                  className={styles.inputLabel}
                  placeholder="Enter title here"
                  value={selectedCustomField.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  disabled={disabledCustomField}
                />
                <Input
                  className={styles.input}
                  placeholder="enter help text..."
                  value={selectedCustomField.helpText || ""}
                  onChange={(e) => handleChange("helpText", e.target.value)}
                />
              </div>
              <div className={styles.actionWrapper}>
                {canDelete && !disabledCustomField && (
                  <Actions.IconButton
                    icon="paperflite-delete"
                    tooltip="Delete this field"
                    onClick={handleDeleteClick}
                  />
                )}
              </div>
            </div>

            <div className={styles.body}>
              <div className={styles.toggleHeader}>Display options</div>
              <div className={styles.toggleRow}>
                <SwitchButton
                  id="multi-select-switch"
                  label="multi select"
                  className={cx(styles.switch, { [styles.active]: selectedCustomField.multiselect })}
                  disabled={size(selectedCustomField.options) < 2}
                  checked={selectedCustomField.multiselect || false}
                  handleCheck={() =>
                    handleChange("multiselect", !selectedCustomField.multiselect)
                  }
                />
                <SwitchButton
                  id="search-page-switch"
                  label="search page"
                  className={cx(styles.switch, { [styles.active]: selectedCustomField.searchable })}
                  checked={selectedCustomField.searchable || false}
                  handleCheck={() =>
                    handleChange("searchable", !selectedCustomField.searchable)
                  }
                />
              </div>
            </div>

            <div className={styles.optionsValue}>List value</div>
            <ListValue
              options={selectedCustomField.options}
              onOptionsChange={(newOptions) =>
                onSelectCustomField({ ...selectedCustomField, options: newOptions })
              }
              disabled={disabledCustomField}
            />

            <DeleteCustomField
              show={showDeleteSlider}
              onClose={() => setShowDeleteSlider(false)}
              selectedCustomField={selectedCustomField}
              deleteCustomField={handleConfirmDelete}
              isDeleting={deleteCustomFieldMutation.isPending}
            />
          </animated.div>
        )
      )}
    </div>
  );
};

export default DetailView;

// This component renders a list of options for a custom field, allowing users to add, edit, and delete options.
interface ListValueProps {
  options: CustomFieldOption[];
  onOptionsChange: (newOptions: CustomFieldOption[]) => void;
  disabled?: boolean;
}

export const ListValue: React.FC<ListValueProps> = ({
  options,
  onOptionsChange,
  disabled = false,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );
  const [activeId, setActiveId] = useState<string | null>(null);

  const activeItem = options.find((item) => item.id === activeId);

  const handleAddValueClick = () => {
    const newOption: CustomFieldOption = {
      id: guid(),
      value: "untitled",
    };
    onOptionsChange([newOption, ...options]);
  };

  // This function handles changes to the value of an option at a specific index.
  const handleValueChange = (index: number, newValue: string) => {
    const updatedOptions = [...options];
    updatedOptions[index] = { ...updatedOptions[index], value: newValue };
    onOptionsChange(updatedOptions);
  };

  // This function handles deleting an option at a specific index.
  const handleDeleteOption = (id: string) => {
    const updated = options.filter((item) => item.id !== id);
    onOptionsChange(updated);
  };

  return (
    <div className={styles.lists}>
      {!disabled && (
      <div className={styles.addListButton} onClick={handleAddValueClick}>
        <i className={cx(styles.icon, "paperflite-add")} />
        <div className={cx(styles.name, styles.add)}>New Value</div>
      </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToWindowEdges]}>
        <ReorderMonitor
          options={options}
          setOptions={onOptionsChange}
          setActiveId={setActiveId}
        />

        <SortableContext
          items={options.map((option) => option.id)}
          strategy={rectSortingStrategy}
        >
          {options.map((item, index) => (
            <SortableListItem
              key={item.id}
              item={item}
              index={index}
              onChange={handleValueChange}
              onDelete={() => handleDeleteOption(item.id)}
              disabled={disabled}
              isDragging={activeId === item.id}
            />
          ))}
        </SortableContext>

        <DragOverlay>
          {activeItem && (
            <div
              className={cx(styles.list, { [styles.disabled]: disabled })}
              style={{ opacity: 0.7, visibility: "visible" }}
            >
              <div className={styles.leftIcons}>
                <div className={styles.listIcon}>
                  <i className="paperflite-drag" />
                </div>
              </div>
              <div className={styles.name}>{activeItem.value}</div>
              <div className={styles.rightIcons}>
                <div className={styles.listActions}>
                  <i className="paperflite-delete" />
                </div>
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

const ReorderMonitor: React.FC<LiveReorderMonitorProps> = ({
  options,
  setOptions,
  setActiveId,
}) => {
  useDndMonitor({
    onDragStart({ active }) {
      setActiveId(active.id as string);
    },
    onDragOver({ active, over }) {
      if (!over || active.id === over.id) return;

      const oldIndex = options.findIndex((item) => item.id === active.id);
      const newIndex = options.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setOptions(arrayMove(options, oldIndex, newIndex));
      }
    },
    onDragEnd() {
      setActiveId(null);
    },
  });

  return null;
};

interface SortableListItemProps {
  item: CustomFieldOption;
  index: number;
  onChange: (index: number, value: string) => void;
  onDelete: (index: number) => void;
  disabled: boolean;
  isDragging: boolean;
}

const SortableListItem: React.FC<SortableListItemProps> = ({
  item,
  index,
  onChange,
  onDelete,
  disabled,
  isDragging,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const transformStyles = {
    transform: CSS.Translate.toString(transform),
    transition: isDragging ? "none" : transition,
    cursor: disabled ? "not-allowed" : "move"
  };

  return (
    <div
    ref={setNodeRef}
    {...(!disabled ? { ...attributes, ...listeners } : {})}
    style={transformStyles}
    className={cx(styles.list, {
      [styles.dragging]: isDragging,
      [styles.disabled]: disabled,
      [styles.hiddenOriginal]: isDragging, 
    })}
  >
      {!disabled && (
        <div className={cx(styles.leftIcons, styles.iconWrapper)}>
          <div className={styles.listIcon}>
            <i className="paperflite-drag" />
          </div>
        </div>
      )}
      <AutoGrowInput
        className={styles.name}
        value={item.value}
        onChange={(e) => onChange(index, e.target.value)}
        disabled={disabled}
      />
      {!disabled && (
        <>
          <div className={styles.divider} />
          <div className={cx(styles.rightIcons, styles.iconWrapper)}>
            <div className={styles.listActions} onClick={() => onDelete(index)}>
              <i className="paperflite-delete" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};